import { useState } from "react";
import {
  DialogContent,
  Dialog,
  CardHeader,
} from "../../../../components";
import { useImportContext } from "../../../../contexts/import.context";
import { CfopService, DocumentoFreteService, history, notification, PessoaService } from "../../../../services";
import { convertXmlToJson, responseErros } from "../../../../utils";
import { parseXml } from "../../documento-frete";
import { FormatCte } from "../entities/format-xml-cte.entities";
import { FormatTransportadora } from "../entities/format-xml-transportadora.entities";
import { ImportarXmlStep } from "./importar-xml-step.component";
import { CadastrarTransportadoraStep } from "./cadastrar-transportadora-step.component";

const ImportarXmlDialog = ({
  openImportarXMLDialog,
  setOpenImportarXMLDialog,
  setLoading,
}) => {
  const { docFreteDto, setDocFreteDto } = useImportContext();
  const [arquivoXml, setArquivoXml] = useState(null);
  const [transportadora, setTransportadora] = useState(null);
  const cfopService = new CfopService();
  const pessoaService = new PessoaService();
  const documentoFreteService = new DocumentoFreteService();
  
  const buscarCfops = async (filtros) => {
    const filtro = {
      ...filtros,
      limite: 20,
    };
    const result = await cfopService.getAll(filtro);
    if (!result.isAxiosError) {
      return result.data.rows;
    } else {
      responseErros(result);
    }
  };

  async function buscarTransportadora(transportadora) {
    try {
      const filtro = {
        cnpjCpf: transportadora?.CNPJ,
      };
      const result = await pessoaService.getAll(filtro);
      if (!result.isAxiosError) {
        if (result.data?.rows.length) {
          return result.data.rows[0];
        } 
        return null;
      } else {
        throw new Error(
          result.response?.data?.message || "Erro ao buscar transportadora"
        );
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }
  
  async function cadastrarTransportadora() {
    setLoading(true);
    try {
      const body = {
        ...transportadora,
        transportadora: transportadora?.endereco,
      };
      const result = await pessoaService.cadastrarPessoaConfirmacao(body);
      if (!result.isAxiosError) {
        notification.cadastroSucesso();
        setDocFreteDto((prevState) => ({
          ...prevState,
          transportadorId: result.data.id
        }));
        setLoading(false);
        history.push("/estoque/documentos-frete/create");
        return;
      } else {
        setLoading(false);
        responseErros(result);
        throw new Error(result.response?.data?.message);
      }
    } catch (error) {
      setLoading(false);
      throw new Error(error.message);
    }
  }

  const extrairDadosCTe = (xmlData) => {
    const {
      cteProc: {
        CTe,
        protCTe,
      },
    } = convertXmlToJson(xmlData);
    if (!CTe || !protCTe?.infProt) {
      initXmlClose();
      return notification.alertaGenericos(
        "Por favor, inserir XML referente a CT-e"
      );
    }
    const objetoFormatDto = {
      frete: CTe.infCte?.ide,
      itens: !Array.isArray(CTe.infCte?.infCTeNorm?.infDoc) 
        ? [CTe.infCte?.infCTeNorm?.infDoc]
        : CTe.infCte?.infCTeNorm?.infDoc,
      transportadora: CTe.infCte?.emit,
      valorPrestado: CTe.infCte?.vPrest,
    }
    return objetoFormatDto;
  };

  const validarDocumento = async (dados, transportadorId) => {
    const numeroDocumento = dados.frete.nCT, serieDocumento = dados.frete.serie;
    const filtros = { 
      numeroDocumento, 
      serieDocumento,
      transportadorId,
      restritiva: true
    };
    const result = await documentoFreteService.getAll(filtros);
    return result.data.count === 0;
  }

  const handleFileRead = async (e) => {
    const content = e.target.result;
    setLoading(true);
    const xmlData = parseXml(content.replace(/>\s+</g, "><").trim());
    const dadosCTe = extrairDadosCTe(xmlData);
    let transportadora = await buscarTransportadora(dadosCTe.transportadora);
    const filtros = {
      codigo: dadosCTe.frete.CFOP
    };
    const cfop = await buscarCfops(filtros);
    if (!transportadora) {
      const formatTransportadora = new FormatTransportadora(dadosCTe.transportadora);
      const formatDocumentoFrete = new FormatCte(dadosCTe, cfop, null);
      setDocFreteDto(formatDocumentoFrete);
      setTransportadora(formatTransportadora);
      setLoading(false);
    } else {
      const docValido = await validarDocumento(dadosCTe, transportadora.id);
      if (!docValido) {
        notification.alertaGenericos(
          "Um documento de frete com o mesmo número de identificação já foi lançado anteriormente."
        );
        setLoading(false);
        return;
      }
      const formatDocumentoFrete = new FormatCte(dadosCTe, cfop, transportadora.id);
      setDocFreteDto(formatDocumentoFrete);
      setLoading(false);
      history.push("/estoque/documentos-frete/create");
    }
  };

  const buscarXmlData = () => {
    const reader = new FileReader();
    reader.onloadend = handleFileRead;
    reader.readAsText(arquivoXml);
  };

  async function handleImportarXml() {
    if (!arquivoXml) {
      return notification.alertaGenericos(
        "Por favor, selecione um arquivo XML"
      );
    }
    buscarXmlData();
    setLoading(false);
  }

  function handleXmlChange(event) {
    if (event.target) {
      setArquivoXml(event.target.files[0]);
    } else {
      setArquivoXml(event[0]);
    }
  }

  function initXmlClose() {
    setArquivoXml(null);
    setOpenImportarXMLDialog(false);
  }

  return (
    <Dialog
      maxWidth={!transportadora ? "sm" : "md"}
      fullWidth
      open={openImportarXMLDialog}
      onClose={initXmlClose}
    >
      <CardHeader 
        title={!transportadora ? "Importar XML" : "Cadastrar Transportadora"} 
        className="m-2" 
      />
      <DialogContent>
        {!transportadora
        ? <ImportarXmlStep 
          arquivoXml={arquivoXml}
          initXmlClose={initXmlClose}
          handleXmlChange={handleXmlChange}
          handleImportarXml={handleImportarXml}
          />
        : <CadastrarTransportadoraStep 
          transportadora={transportadora}
          setTransportadora={setTransportadora}
          cadastrarTransportadora={cadastrarTransportadora}
          docFreteDto={docFreteDto}
          setDocFreteDto={setDocFreteDto}
          />
        }
      </DialogContent>
    </Dialog>
  );
};

export default ImportarXmlDialog;
